<template>
  <div class="repair-type-user-container">
    <!-- (1) PageHeader -->
    <ds-page-header :title="$t('repairTypeUser.title')" :subtitle="$t('repairTypeUser.page.subtitle')">
      <template #extra>
        <el-button icon="el-icon-back" @click="_goBack">
          {{ $t('common.back') }}
        </el-button>
        <el-button type="primary" icon="el-icon-plus" @click="_openAddRepairTypeUserModal">
          {{ $t('common.add') }}
        </el-button>
      </template>
    </ds-page-header>

    <!-- (3)(4)(6) Toolbar + DataTable + PaginationBar -->
    <div class="ds-panel">
      <ds-toolbar :total="repairTypeUserManageInfo.total" :density="density" :show-column-setting="false" :show-refresh="true"
        @update:density="density = $event" @refresh="_listRepairTypeUsers(currentPage, pageSize)">
        <template #left>
          <span class="ds-panel__title">{{ $t('repairTypeUser.title') }}</span>
          <span v-if="repairTypeUserManageInfo.repairTypeName" class="ds-text-muted">
            · {{ repairTypeUserManageInfo.repairTypeName }}
          </span>
        </template>
      </ds-toolbar>

      <ds-data-table :data="repairTypeUserManageInfo.repairTypeUsers" :loading="loading" :error="hasError"
        :error-message="errorMessage" :density="density" border :empty-description="$t('repairTypeUser.page.empty')"
        @retry="_listRepairTypeUsers(currentPage, pageSize)">
        <el-table-column prop="staffId" :label="$t('repairTypeUser.repairMasterId')" align="center" width="140">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.staffId || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="staffName" :label="$t('repairTypeUser.masterName')" align="center" min-width="140">
          <template slot-scope="scope">
            {{ scope.row.staffName || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="repairTypeName" :label="$t('repairTypeUser.repairType')" align="center" min-width="140"
          show-overflow-tooltip>
          <template slot-scope="scope">
            {{ scope.row.repairTypeName || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="stateName" :label="$t('repairTypeUser.status')" align="center" width="110">
          <template slot-scope="scope">
            <ds-status-tag :label="scope.row.stateName || '-'" :text="scope.row.stateName" :type="getStateType(scope.row.state)" />
          </template>
        </el-table-column>
        <el-table-column prop="remark" :label="$t('repairTypeUser.description')" align="center" min-width="140"
          show-overflow-tooltip>
          <template slot-scope="scope">
            {{ scope.row.remark || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="createTime" :label="$t('repairTypeUser.createTime')" align="center" width="160">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.createTime || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('common.operation')" align="center" width="160" fixed="right">
          <template slot-scope="scope">
            <el-button-group>
              <el-button size="mini" @click="_openDeleteTypeUserModel(scope.row)">
                {{ $t('common.delete') }}
              </el-button>
              <el-button size="mini" @click="_openEditrepairTypeUserModel(scope.row)">
                {{ $t('common.change') }}
              </el-button>
            </el-button-group>
          </template>
        </el-table-column>
      </ds-data-table>

      <ds-pagination-bar :total="repairTypeUserManageInfo.total" :page="currentPage" :size="pageSize"
        :page-sizes="[10, 20, 30, 50]" @size-change="handleSizeChange" @current-change="handleCurrentChange" />
    </div>

    <SelectStaff ref="selectStaff" @selectStaff="handleSelectStaff" />
    <DeleteRepairTypeUser ref="deleteRepairTypeUser" @success="handleDeleteSuccess" />
    <EditRepairTypeUser ref="editRepairTypeUser" @success="handleEditSuccess" />
  </div>
</template>

<script>
import { listRepairTypeUsers, saveRepairTypeUser } from '@/api/work/repairTypeUserApi'
import { getCommunityId } from '@/api/community/communityApi'
import SelectStaff from '@/components/staff/SelectStaff'
import DeleteRepairTypeUser from '@/components/work/DeleteRepairTypeUser'
import EditRepairTypeUser from '@/components/work/EditRepairTypeUser'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsPaginationBar from '@/components/ds/ds-pagination-bar.vue'
import DsStatusTag from '@/components/ds/ds-status-tag.vue'

export default {
  name: 'RepairTypeUserList',
  components: {
    SelectStaff,
    DeleteRepairTypeUser,
    EditRepairTypeUser,
    DsPageHeader,
    DsToolbar,
    DsDataTable,
    DsPaginationBar,
    DsStatusTag
  },
  data() {
    return {
      loading: false,
      hasError: false,
      errorMessage: '',
      density: 'default',
      repairTypeUserManageInfo: {
        repairTypeUsers: [],
        total: 0,
        records: 1,
        repairType: '',
        repairTypeName: '',
        conditions: {
          repairTypeName: '',
          repairWay: '',
          repairType: ''
        }
      },
      currentPage: 1,
      pageSize: 10
    }
  },
  created() {
    this.repairTypeUserManageInfo.repairType = this.$route.query.repairType
    this.repairTypeUserManageInfo.repairTypeName = this.$route.query.repairTypeName
    this._listRepairTypeUsers(this.currentPage, this.pageSize)
  },
  methods: {
    async _listRepairTypeUsers(page, size) {
      this.loading = true
      this.hasError = false
      this.errorMessage = ''
      try {
        const communityId = await getCommunityId()
        const params = {
          page: page,
          row: size,
          communityId: communityId,
          repairType: this.repairTypeUserManageInfo.repairType
        }

        const response = await listRepairTypeUsers(params)
        if (response.code === 0) {
          this.repairTypeUserManageInfo.repairTypeUsers = response.data
          this.repairTypeUserManageInfo.total = response.total
        } else {
          this.$message.error(response.msg || this.$t('repairTypeUser.page.loadFailed'))
        }
      } catch (error) {
        this.hasError = true
        this.errorMessage = this.$t('repairTypeUser.page.loadFailed')
        console.error('Error fetching repair type users:', error)
        this.$message.error(this.$t('repairTypeUser.page.loadFailed'))
      } finally {
        this.loading = false
      }
    },
    getStateType(state) {
      return state === '1' ? 'success' : 'muted'
    },
    handleSizeChange(val) {
      this.pageSize = val
      this.currentPage = 1
      this._listRepairTypeUsers(this.currentPage, val)
    },
    handleCurrentChange(val) {
      this.currentPage = val
      this._listRepairTypeUsers(val, this.pageSize)
    },
    _openAddRepairTypeUserModal() {
      this.$refs.selectStaff.open()
    },
    _openEditrepairTypeUserModel(row) {
      this.$refs.editRepairTypeUser.open(row)
    },
    _openDeleteTypeUserModel(row) {
      this.$refs.deleteRepairTypeUser.open(row)
    },
    _goBack() {
      this.$router.go(-1)
    },
    handleSelectStaff(staff) {
      this.saveRepairTypeUserInfo(staff)
    },
    async saveRepairTypeUserInfo(staff) {
      try {
        const communityId = await getCommunityId()
        const data = {
          communityId: communityId,
          staffId: staff.staffId,
          staffName: staff.staffName,
          repairType: this.repairTypeUserManageInfo.repairType
        }

        const response = await saveRepairTypeUser(data)
        if (response.code === 0) {
          this.$message.success(this.$t('common.operationSuccess'))
          this._listRepairTypeUsers(this.currentPage, this.pageSize)
        } else {
          this.$message.error(response.msg || this.$t('repairTypeUser.page.saveFailed'))
        }
      } catch (error) {
        console.error('Error saving repair type user:', error)
        this.$message.error(this.$t('repairTypeUser.page.saveFailed'))
      }
    },
    handleDeleteSuccess() {
      this._listRepairTypeUsers(this.currentPage, this.pageSize)
    },
    handleEditSuccess() {
      this._listRepairTypeUsers(this.currentPage, this.pageSize)
    }
  }
}
</script>

<style lang="scss" scoped>
.repair-type-user-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

/* 面板标题 */
.ds-panel__title {
  font-size: var(--ds-font-size-lg);
  font-weight: 600;
  color: var(--ds-gray-800);
}
</style>
